import { useState, useEffect, useCallback, useMemo } from "react";
import apiClient from "../services/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  Search,
  X,
  Trash2,
  Eye,
  Pencil,
  Link2,
  DollarSign,
  Store,
  Calendar,
  User,
  FileText,
  Loader2,
  ChevronDown,
  ChevronRight,
  Plus,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { fuzzyMatch } from "../lib/searchUtils";
import { formatCurrency } from "../lib/formatters";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
const ReceiptsPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [receipts, setReceipts] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [collapsedYears, setCollapsedYears] = useState({});
  const [collapsedMonths, setCollapsedMonths] = useState({});
  const [showPendingOnly, setShowPendingOnly] = useState(false);

  // Dialogs
  const [viewReceipt, setViewReceipt] = useState(null);
  const [editReceipt, setEditReceipt] = useState(null);
  const [assignReceipt, setAssignReceipt] = useState(null);
  const [selectedJobId, setSelectedJobId] = useState("");
  const [saving, setSaving] = useState(false);

  // Edit form state
  const [editForm, setEditForm] = useState({
    store_name: "",
    subtotal: "",
    sales_tax: "",
    total: "",
  });

  const canManage = ["admin", "project_manager", "office_manager"].includes(user?.role);

  // Manual receipt entry state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    store_name: "",
    description: "",
    subtotal: "",
    sales_tax: "",
    total: "",
  });
  const [addSaving, setAddSaving] = useState(false);

  const handleAddReceipt = async (e) => {
    e.preventDefault();
    setAddSaving(true);
    try {
      await apiClient.post(`/receipts/manual`, {
        store_name: addForm.store_name,
        description: addForm.description,
        subtotal: parseFloat(addForm.subtotal) || 0,
        sales_tax: parseFloat(addForm.sales_tax) || 0,
        total: parseFloat(addForm.total) || 0,
      });
      toast.success("Receipt added");
      setAddDialogOpen(false);
      setAddForm({ store_name: "", description: "", subtotal: "", sales_tax: "", total: "" });
      fetchReceipts();
    } catch {
      toast.error("Failed to add receipt");
    } finally {
      setAddSaving(false);
    }
  };

  const fetchReceipts = useCallback(async () => {
    try {
      const res = await apiClient.get(`/receipts`);
      setReceipts(res.data || []);
    } catch {
      toast.error("Failed to load receipts");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await apiClient.get(`/jobs`);
      // Filter to active/precon jobs only for assignment
      setJobs((res.data || []).filter(j => j.status === "active" || j.status === "precon"));
    } catch (err) {
      // Jobs list is optional for receipt assignment — log but don't block
      console.error("ReceiptsPage: failed to fetch jobs:", err);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchReceipts();
    fetchJobs();
  }, [fetchReceipts, fetchJobs]);

  // Auto-refresh while any receipts are still processing
  useEffect(() => {
    const hasProcessing = receipts.some(r => r.ai_status === "processing");
    if (hasProcessing) {
      const interval = setInterval(() => {
        fetchReceipts();
      }, 3000); // Poll every 3 seconds
      return () => clearInterval(interval);
    }
  }, [receipts, fetchReceipts]);

  // Format date for display in table (short format: "Apr 1, 2026")
  const formatReceiptDate = (isoStr) => {
    if (!isoStr) return "";
    const d = new Date(isoStr);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const filtered = useMemo(() => {
    return receipts.filter((r) => {
      if (showPendingOnly && r.reconciled !== false) return false;
      if (!search.trim()) return true;
      const searchable = [
        r.store_name,
        r.uploaded_by,
        r.job_name,
        r.description,
        formatReceiptDate(r.uploaded_at),
      ].filter(Boolean).join(" ");
      return fuzzyMatch(search, searchable);
    });
  }, [receipts, search, showPendingOnly]);

  const pendingReconcileCount = useMemo(
    () => receipts.filter((r) => r.reconciled === false).length,
    [receipts]
  );

  // Group receipts by year, then by month (newest first)
  const groupedByYear = useMemo(() => {
    // Sort by date descending (newest first)
    const sorted = [...filtered].sort((a, b) => {
      const dateA = new Date(a.uploaded_at || 0);
      const dateB = new Date(b.uploaded_at || 0);
      return dateB - dateA;
    });

    // Group by year first, then by month
    const years = {};
    sorted.forEach((receipt) => {
      const date = new Date(receipt.uploaded_at);
      const year = date.getFullYear().toString();
      const month = date.toLocaleDateString("en-US", { month: "long" });
      const monthKey = `${year}-${month}`;

      if (!years[year]) {
        years[year] = {
          year,
          months: {},
          total: 0,
          count: 0,
        };
      }

      if (!years[year].months[monthKey]) {
        years[year].months[monthKey] = {
          label: month,
          key: monthKey,
          receipts: [],
          total: 0,
        };
      }

      years[year].months[monthKey].receipts.push(receipt);
      years[year].months[monthKey].total += parseFloat(receipt.total) || 0;
      years[year].total += parseFloat(receipt.total) || 0;
      years[year].count += 1;
    });

    // Convert to array and sort months within each year (newest first)
    return Object.values(years).map((yearGroup) => ({
      ...yearGroup,
      months: Object.values(yearGroup.months),
    }));
  }, [filtered]);

  const toggleYear = (year) => {
    setCollapsedYears((prev) => ({
      ...prev,
      [year]: !prev[year],
    }));
  };

  const toggleMonth = (monthKey) => {
    setCollapsedMonths((prev) => ({
      ...prev,
      [monthKey]: !prev[monthKey],
    }));
  };

  // View PDF in new tab
  const handleViewPdf = (receipt) => {
    window.open(`/api/receipts/${receipt.id}/file`, "_blank");
  };

  // Open Edit dialog
  const handleOpenEdit = (receipt) => {
    setEditForm({
      store_name: receipt.store_name || "",
      subtotal: receipt.subtotal?.toString() || "",
      sales_tax: receipt.sales_tax?.toString() || "",
      total: receipt.total?.toString() || "",
    });
    setEditReceipt(receipt);
  };

  // Save Edit
  const handleSaveEdit = async () => {
    if (!editReceipt) return;
    setSaving(true);
    try {
      await apiClient.put(
        `/receipts/${editReceipt.id}`,
        {
          store_name: editForm.store_name.trim(),
          subtotal: parseFloat(editForm.subtotal) || 0,
          sales_tax: parseFloat(editForm.sales_tax) || 0,
          total: parseFloat(editForm.total) || 0,
        },
        { headers: { "Content-Type": "application/json" } }
      );
      toast.success("Receipt updated");
      setEditReceipt(null);
      fetchReceipts();
    } catch {
      toast.error("Failed to update receipt");
    } finally {
      setSaving(false);
    }
  };

  // Open Assign dialog
  const handleOpenAssign = (receipt) => {
    setSelectedJobId(receipt.job_id || "");
    setAssignReceipt(receipt);
  };

  // Save Assignment
  const handleSaveAssign = async () => {
    if (!assignReceipt) return;
    setSaving(true);
    try {
      if (selectedJobId) {
        await apiClient.post(
          `/receipts/${assignReceipt.id}/assign`,
          { job_id: selectedJobId },
          { headers: { "Content-Type": "application/json" } }
        );
        toast.success("Receipt assigned to job");
      } else if (assignReceipt.job_id) {
        // Unassign
        await apiClient.post(
          `/receipts/${assignReceipt.id}/unassign`,
          {},
          { headers: { "Content-Type": "application/json" } }
        );
        toast.success("Receipt unassigned");
      }
      setAssignReceipt(null);
      fetchReceipts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update assignment");
    } finally {
      setSaving(false);
    }
  };

  // Reconcile a receipt — pushes it onto the assigned job's supplier_invoices
  const handleReconcile = async (receipt) => {
    if (!receipt?.job_id) {
      toast.error("Assign the receipt to a job before reconciling");
      return;
    }
    try {
      await apiClient.post(`/receipts/${receipt.id}/reconcile`);
      toast.success(`Receipt reconciled to ${receipt.job_name}`);
      fetchReceipts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reconcile receipt");
    }
  };

  // Delete
  const handleDelete = async (receipt) => {
    if (!window.confirm("Delete this receipt? This cannot be undone.")) return;
    try {
      await apiClient.delete(`/receipts/${receipt.id}`);
      toast.success("Receipt deleted");
      fetchReceipts();
    } catch {
      toast.error("Failed to delete receipt");
    }
  };

  // Render a single receipt row
  const renderReceiptRow = (receipt) => (
    <TableRow
      key={receipt.id}
      className="hover:bg-muted/30"
      data-testid={`receipt-row-${receipt.id}`}
    >
      <TableCell className="font-medium text-sm">
        {formatReceiptDate(receipt.uploaded_at)}
      </TableCell>
      <TableCell className="text-sm">{receipt.uploaded_by || "—"}</TableCell>
      <TableCell className="text-sm">
        {receipt.ai_status === "processing" ? (
          <span className="text-blue-600 italic flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" />
            Analyzing...
          </span>
        ) : (
          <div>
            <div>{receipt.store_name || <span className="text-muted-foreground italic">Not detected</span>}</div>
            {receipt.description && (
              <div className="text-xs text-muted-foreground truncate max-w-[200px]" title={receipt.description}>
                {receipt.description}
              </div>
            )}
          </div>
        )}
      </TableCell>
      <TableCell className="text-right text-sm tabular-nums">
        {receipt.ai_status === "processing" ? (
          <span className="text-muted-foreground">—</span>
        ) : receipt.subtotal ? formatCurrency(receipt.subtotal, true) : "—"}
      </TableCell>
      <TableCell className="text-right text-sm tabular-nums">
        {receipt.ai_status === "processing" ? (
          <span className="text-muted-foreground">—</span>
        ) : receipt.sales_tax ? formatCurrency(receipt.sales_tax, true) : "—"}
      </TableCell>
      <TableCell className="text-right text-sm font-semibold tabular-nums">
        {receipt.ai_status === "processing" ? (
          <span className="text-muted-foreground">—</span>
        ) : receipt.total ? formatCurrency(receipt.total, true) : "—"}
      </TableCell>
      <TableCell>
        {receipt.job_name ? (
          <div className="flex flex-col gap-1">
            <Badge variant="secondary" className="text-xs font-normal truncate max-w-[120px]">
              {receipt.job_name}
            </Badge>
            {receipt.reconciled === false && (
              <Badge
                variant="outline"
                className="text-xs font-normal bg-amber-50 text-amber-800 border-amber-300"
                data-testid={`pending-reconcile-badge-${receipt.id}`}
              >
                <AlertCircle className="w-3 h-3 mr-1" />
                Needs Reconciliation
              </Badge>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground text-xs">Unassigned</span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center justify-center gap-1">
          {/* View PDF */}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => handleViewPdf(receipt)}
            title="View PDF"
            data-testid={`view-btn-${receipt.id}`}
          >
            <Eye className="w-4 h-4" />
          </Button>

          {/* Edit */}
          {canManage && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => handleOpenEdit(receipt)}
              title="Edit"
              data-testid={`edit-btn-${receipt.id}`}
            >
              <Pencil className="w-4 h-4" />
            </Button>
          )}

          {/* Assign to Job */}
          {canManage && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => handleOpenAssign(receipt)}
              title={receipt.job_id ? "Reassign/Unassign" : "Assign to Job"}
              data-testid={`assign-btn-${receipt.id}`}
            >
              {receipt.job_id ? (
                <Link2 className="w-4 h-4 text-blue-600" />
              ) : (
                <Link2 className="w-4 h-4" />
              )}
            </Button>
          )}

          {/* Reconcile (only when pending) */}
          {canManage && receipt.reconciled === false && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
              onClick={() => handleReconcile(receipt)}
              title="Reconcile & apply to job"
              data-testid={`reconcile-btn-${receipt.id}`}
            >
              <CheckCircle2 className="w-4 h-4" />
            </Button>
          )}

          {/* Delete */}
          {canManage && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={() => handleDelete(receipt)}
              title="Delete"
              data-testid={`delete-btn-${receipt.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="receipts-loading">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="page-enter space-y-6" data-testid="receipts-page">
      {/* Header */}
      <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-4xl font-bold tracking-tight text-foreground">Receipts</h1>
        <div className="flex items-center gap-3">
          {canManage && (
            <Button
              onClick={() => setAddDialogOpen(true)}
              data-testid="add-receipt-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Receipt
            </Button>
          )}
          <span className="text-sm text-muted-foreground">
            {filtered.length} receipt{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by store, date, or uploader..."
            className="pl-9 pr-9"
            data-testid="receipts-search"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {pendingReconcileCount > 0 && (
          <Button
            type="button"
            variant={showPendingOnly ? "default" : "outline"}
            size="sm"
            onClick={() => setShowPendingOnly((v) => !v)}
            className={showPendingOnly ? "bg-amber-600 hover:bg-amber-700" : "border-amber-300 text-amber-800 hover:bg-amber-50"}
            data-testid="pending-reconcile-filter"
          >
            <AlertCircle className="w-4 h-4 mr-2" />
            Pending Reconciliation ({pendingReconcileCount})
          </Button>
        )}
      </div>

      {/* Receipt Groups by Year > Month */}
      {filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {receipts.length === 0
              ? "No receipts yet. Use 'Add Receipt' for manual entry or upload receipts from the Daily Entry Photos tab."
              : "No receipts match your search."}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {groupedByYear.map((yearGroup) => {
            const isYearCollapsed = collapsedYears[yearGroup.year];
            return (
              <Card key={yearGroup.year} data-testid={`year-group-${yearGroup.year}`}>
                <Collapsible open={!isYearCollapsed} onOpenChange={() => toggleYear(yearGroup.year)}>
                  <CollapsibleTrigger asChild>
                    <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors border-b bg-muted/20">
                      <div className="flex items-center gap-3">
                        {isYearCollapsed ? (
                          <ChevronRight className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        )}
                        <span className="font-bold text-xl">{yearGroup.year}</span>
                        <Badge variant="outline" className="ml-2">
                          {yearGroup.count} receipt{yearGroup.count !== 1 ? "s" : ""}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-muted-foreground">Year Total:</span>
                        <span className="font-bold tabular-nums">{formatCurrency(yearGroup.total, true)}</span>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="space-y-2 p-2">
                      {yearGroup.months.map((monthGroup) => {
                        const isMonthCollapsed = collapsedMonths[monthGroup.key];
                        return (
                          <div key={monthGroup.key} className="border rounded-lg" data-testid={`month-group-${monthGroup.key}`}>
                            <Collapsible open={!isMonthCollapsed} onOpenChange={() => toggleMonth(monthGroup.key)}>
                              <CollapsibleTrigger asChild>
                                <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/30 transition-colors">
                                  <div className="flex items-center gap-3">
                                    {isMonthCollapsed ? (
                                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                    ) : (
                                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                    )}
                                    <div className="flex items-center gap-2">
                                      <Calendar className="w-4 h-4 text-muted-foreground" />
                                      <span className="font-semibold">{monthGroup.label}</span>
                                    </div>
                                    <Badge variant="secondary" className="text-xs">
                                      {monthGroup.receipts.length} receipt{monthGroup.receipts.length !== 1 ? "s" : ""}
                                    </Badge>
                                  </div>
                                  <div className="flex items-center gap-2 text-sm">
                                    <span className="text-muted-foreground">Total:</span>
                                    <span className="font-semibold tabular-nums">{formatCurrency(monthGroup.total, true)}</span>
                                  </div>
                                </div>
                              </CollapsibleTrigger>
                              <CollapsibleContent>
                                <div className="overflow-x-auto border-t">
                                  <Table>
                                    <TableHeader>
                                      <TableRow className="bg-muted/30">
                                        <TableHead className="w-[110px]">
                                          <div className="flex items-center gap-1">
                                            <Calendar className="w-3.5 h-3.5" />
                                            Date
                                          </div>
                                        </TableHead>
                                        <TableHead>
                                          <div className="flex items-center gap-1">
                                            <User className="w-3.5 h-3.5" />
                                            Uploaded By
                                          </div>
                                        </TableHead>
                                        <TableHead>
                                          <div className="flex items-center gap-1">
                                            <Store className="w-3.5 h-3.5" />
                                            Store
                                          </div>
                                        </TableHead>
                                        <TableHead className="text-right w-[100px]">Subtotal</TableHead>
                                        <TableHead className="text-right w-[100px]">Sales Tax</TableHead>
                                        <TableHead className="text-right w-[100px]">
                                          <div className="flex items-center justify-end gap-1">
                                            <DollarSign className="w-3.5 h-3.5" />
                                            Total
                                          </div>
                                        </TableHead>
                                        <TableHead className="w-[140px]">Assigned Job</TableHead>
                                        <TableHead className="w-[120px] text-center">Actions</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {monthGroup.receipts.map(renderReceiptRow)}
                                    </TableBody>
                                  </Table>
                                </div>
                              </CollapsibleContent>
                            </Collapsible>
                          </div>
                        );
                      })}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            );
          })}
        </div>
      )}

      {/* View PDF Dialog */}
      <Dialog open={!!viewReceipt} onOpenChange={(o) => !o && setViewReceipt(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {viewReceipt?.original_name || "Receipt"}
            </DialogTitle>
          </DialogHeader>
          {viewReceipt && (
            <iframe
              src={`/api/receipts/${viewReceipt.id}/file`}
              title={viewReceipt.original_name}
              className="w-full rounded-lg border"
              style={{ height: "70vh" }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Receipt Dialog */}
      <Dialog open={!!editReceipt} onOpenChange={(o) => !o && setEditReceipt(null)}>
        <DialogContent data-testid="edit-receipt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5" />
              Edit Receipt Data
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Correct AI-extracted values if needed.
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Store Name</label>
                <Input
                  value={editForm.store_name}
                  onChange={(e) => setEditForm({ ...editForm, store_name: e.target.value })}
                  placeholder="Store name"
                  data-testid="edit-store-name"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-sm font-medium mb-1 block">Subtotal</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editForm.subtotal}
                    onChange={(e) => setEditForm({ ...editForm, subtotal: e.target.value })}
                    placeholder="0.00"
                    data-testid="edit-subtotal"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Sales Tax</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editForm.sales_tax}
                    onChange={(e) => setEditForm({ ...editForm, sales_tax: e.target.value })}
                    placeholder="0.00"
                    data-testid="edit-sales-tax"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Total</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editForm.total}
                    onChange={(e) => setEditForm({ ...editForm, total: e.target.value })}
                    placeholder="0.00"
                    data-testid="edit-total"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditReceipt(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving} data-testid="save-edit-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign to Job Dialog */}
      <Dialog open={!!assignReceipt} onOpenChange={(o) => !o && setAssignReceipt(null)}>
        <DialogContent data-testid="assign-receipt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5" />
              Assign Receipt to Job
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Assign this receipt to a job. It will appear in the job's Invoices tab and affect profit/loss calculations.
            </p>
            <div>
              <label className="text-sm font-medium mb-1 block">Select Job</label>
              <Select value={selectedJobId || "unassigned"} onValueChange={(v) => setSelectedJobId(v === "unassigned" ? "" : v)}>
                <SelectTrigger data-testid="job-select">
                  <SelectValue placeholder="Choose a job..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">
                    <span className="text-muted-foreground">— Unassigned —</span>
                  </SelectItem>
                  {jobs.map((job) => (
                    <SelectItem key={job.id} value={job.id}>
                      {job.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {assignReceipt?.job_name && (
              <p className="text-sm">
                Currently assigned to: <strong>{assignReceipt.job_name}</strong>
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignReceipt(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveAssign} disabled={saving} data-testid="save-assign-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {selectedJobId ? "Assign" : "Unassign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Receipt Entry Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Receipt</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddReceipt} className="space-y-4">
            <div className="form-group">
              <label className="text-sm font-medium">Store Name *</label>
              <Input
                value={addForm.store_name}
                onChange={(e) => setAddForm(prev => ({ ...prev, store_name: e.target.value }))}
                placeholder="e.g., Home Depot"
                required
                data-testid="manual-receipt-store"
              />
            </div>
            <div className="form-group">
              <label className="text-sm font-medium">Description</label>
              <Input
                value={addForm.description}
                onChange={(e) => setAddForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="e.g., Concrete mix, rebar"
                data-testid="manual-receipt-description"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="form-group">
                <label className="text-sm font-medium">Subtotal</label>
                <Input
                  type="number"
                  step="0.01"
                  value={addForm.subtotal}
                  onChange={(e) => setAddForm(prev => ({ ...prev, subtotal: e.target.value }))}
                  placeholder="0.00"
                  data-testid="manual-receipt-subtotal"
                />
              </div>
              <div className="form-group">
                <label className="text-sm font-medium">Sales Tax</label>
                <Input
                  type="number"
                  step="0.01"
                  value={addForm.sales_tax}
                  onChange={(e) => setAddForm(prev => ({ ...prev, sales_tax: e.target.value }))}
                  placeholder="0.00"
                  data-testid="manual-receipt-tax"
                />
              </div>
              <div className="form-group">
                <label className="text-sm font-medium">Total *</label>
                <Input
                  type="number"
                  step="0.01"
                  value={addForm.total}
                  onChange={(e) => setAddForm(prev => ({ ...prev, total: e.target.value }))}
                  placeholder="0.00"
                  required
                  data-testid="manual-receipt-total"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={addSaving || !addForm.store_name || !addForm.total} data-testid="manual-receipt-submit">
                {addSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Add Receipt
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReceiptsPage;
